import { Link } from 'react-router-dom';
import { ROUTES } from '@traction/shared';
import {
  ArrowRight,
  DollarSign,
  Target,
  Clock,
  Mic,
  Chrome,
  Rocket,
} from 'lucide-react';

const benefits = [
  {
    icon: DollarSign,
    title: 'Stop paying for agencies',
    description:
      'Sales dev agencies cost $3K-8K per month. Traction Bee gives you an AI SDR for a fraction of the cost. Get the same quality outreach without the enterprise price tag.',
  },
  {
    icon: Target,
    title: 'No more cold outreach guesswork',
    description:
      'AI identifies prospects showing buying signals - funding rounds, hiring patterns, tech adoption - so every email lands with context. No more spray and pray.',
  },
  {
    icon: Clock,
    title: '2 minutes a day, not 2 hours',
    description:
      'Wake up to 3 vetted leads with pre-written emails in your Gmail. Review, tweak if needed, send. Done. Spend your time building, not prospecting.',
  },
  {
    icon: Mic,
    title: 'Your voice, not a template',
    description:
      'Scribe learns your tone and writes emails that sound like you, not a bot. Set your style once, and every draft matches your voice.',
  },
  {
    icon: Chrome,
    title: 'Works where you already work',
    description:
      'Our Chrome extension lives in Gmail. No new dashboard to check, no new tool to learn. Leads appear in your inbox, ready to send with one click.',
  },
  {
    icon: Rocket,
    title: 'Built for founders, not enterprises',
    description:
      'No enterprise pricing, no 6-month contracts, no 50-seat minimums. Start free, cancel anytime. Pricing that makes sense for pre-revenue teams.',
  },
];

export function BenefitsPage() {
  return (
    <div className="py-12 bg-cacao-dark">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="font-heading text-xl font-semibold text-[#F5F5F5]">
            Why Founders Choose Traction Bee
          </h1>
          <p className="mt-2 text-sm text-cacao-muted max-w-xl mx-auto">
            Stop burning hours on manual prospecting. Let AI handle the research and writing while you focus on building your product.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {benefits.map((benefit) => (
            <div
              key={benefit.title}
              className="rounded-lg border border-cacao-dark-tertiary bg-cacao-dark-secondary p-6"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-cacao-dark-tertiary">
                <benefit.icon className="h-5 w-5 text-cacao-gold" />
              </div>
              <h3 className="mt-4 text-sm font-medium text-[#F5F5F5]">
                {benefit.title}
              </h3>
              <p className="mt-2 text-sm text-cacao-muted leading-relaxed">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>

        {/* Comparison Section */}
        <div className="mt-16">
          <h2 className="text-center text-sm font-medium text-cacao-muted uppercase tracking-wide">
            The old way vs. the Traction Bee way
          </h2>
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-cacao-dark-tertiary bg-cacao-dark p-5">
              <p className="text-xs font-medium text-red-400 uppercase tracking-wide">Without Traction Bee</p>
              <ul className="mt-4 space-y-3 text-sm text-cacao-muted">
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-0.5">-</span>
                  2+ hours daily researching leads on LinkedIn
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-0.5">-</span>
                  Generic emails that get ignored
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-0.5">-</span>
                  $3K-8K/month for a sales dev agency
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-0.5">-</span>
                  Juggling multiple tools and spreadsheets
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-0.5">-</span>
                  Inconsistent outreach when you get busy
                </li>
              </ul>
            </div>
            <div className="rounded-lg border border-cacao-gold/30 bg-cacao-dark-secondary p-5">
              <p className="text-xs font-medium text-cacao-gold uppercase tracking-wide">With Traction Bee</p>
              <ul className="mt-4 space-y-3 text-sm text-cacao-muted">
                <li className="flex items-start gap-2">
                  <span className="text-cacao-gold mt-0.5">+</span>
                  2 minutes to review and send 3 leads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cacao-gold mt-0.5">+</span>
                  Personalized emails based on real research
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cacao-gold mt-0.5">+</span>
                  10x cheaper than agencies
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cacao-gold mt-0.5">+</span>
                  Everything in Gmail, where you already work
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cacao-gold mt-0.5">+</span>
                  Consistent pipeline, even when you are heads down
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Competitor Comparison Section */}
        <div className="mt-16">
          <h2 className="text-center text-sm font-medium text-cacao-muted uppercase tracking-wide">
            vs. The Alternatives
          </h2>
          <div className="mt-8 overflow-x-auto rounded-lg border border-cacao-dark-tertiary">
            <table className="w-full text-sm min-w-[500px]">
              <thead>
                <tr className="border-b border-cacao-dark-tertiary bg-cacao-dark-tertiary">
                  <th className="px-4 py-3 text-left font-medium text-cacao-muted"></th>
                  <th className="px-4 py-3 text-center font-medium text-cacao-gold">Traction Bee</th>
                  <th className="px-4 py-3 text-center font-medium text-cacao-muted">Apollo.io</th>
                  <th className="px-4 py-3 text-center font-medium text-cacao-muted">Clay</th>
                </tr>
              </thead>
              <tbody className="bg-cacao-dark-secondary">
                <tr className="border-b border-cacao-dark-tertiary">
                  <td className="px-4 py-3 text-cacao-muted">Lead discovery</td>
                  <td className="px-4 py-3 text-center text-green-400">Buying signals</td>
                  <td className="px-4 py-3 text-center text-cacao-text-tertiary">Database only</td>
                  <td className="px-4 py-3 text-center text-cacao-text-tertiary">BYO list</td>
                </tr>
                <tr className="border-b border-cacao-dark-tertiary">
                  <td className="px-4 py-3 text-cacao-muted">Email writing</td>
                  <td className="px-4 py-3 text-center text-green-400">AI + learns voice</td>
                  <td className="px-4 py-3 text-center text-cacao-text-tertiary">Manual</td>
                  <td className="px-4 py-3 text-center text-cacao-text-tertiary">Templates</td>
                </tr>
                <tr className="border-b border-cacao-dark-tertiary">
                  <td className="px-4 py-3 text-cacao-muted">Shared connections</td>
                  <td className="px-4 py-3 text-center text-green-400">Auto-detected</td>
                  <td className="px-4 py-3 text-center text-cacao-text-tertiary">-</td>
                  <td className="px-4 py-3 text-center text-cacao-text-tertiary">Manual setup</td>
                </tr>
                <tr className="border-b border-cacao-dark-tertiary">
                  <td className="px-4 py-3 text-cacao-muted">Setup time</td>
                  <td className="px-4 py-3 text-center text-green-400">5 minutes</td>
                  <td className="px-4 py-3 text-center text-cacao-text-tertiary">30+ min</td>
                  <td className="px-4 py-3 text-center text-cacao-text-tertiary">8+ hours</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-cacao-muted">Starting price</td>
                  <td className="px-4 py-3 text-center text-green-400 font-medium">$49/mo</td>
                  <td className="px-4 py-3 text-center text-cacao-text-tertiary">$59/mo</td>
                  <td className="px-4 py-3 text-center text-cacao-text-tertiary">$99/mo</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-center text-xs text-cacao-text-tertiary">
            Prices as of March 2025. Traction Bee includes lead discovery, email writing, and Gmail integration.
          </p>
        </div>

        {/* CTA Section */}
        <div className="mt-16 rounded-lg bg-cacao-dark-secondary border border-cacao-dark-tertiary p-6 sm:p-8 text-center">
          <h2 className="font-heading text-lg font-medium text-[#F5F5F5]">
            Ready to reclaim your time?
          </h2>
          <p className="mt-2 text-sm text-cacao-muted">
            Start your 14-day free trial. No credit card required.
          </p>
          <div className="mt-6">
            <Link
              to={ROUTES.SIGNUP}
              className="inline-flex items-center gap-1.5 rounded-md bg-cacao-gold px-5 py-2.5 text-sm font-medium text-cacao-dark hover:bg-cacao-gold-hover transition-colors"
            >
              Start free trial
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
